// src/pages/guides/DNSDriftGuide.js
// DNS Drift Guide (G2) - Supporting guide for Configuration Drift pillar

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function DNSDriftGuide() {
  const startTrial = useTrialCta('guide_trial:dns-drift-detection');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "DNS Drift: How Attackers Exploit Stale Records",
    "description": "Discover how DNS drift — dangling CNAMEs, stale MX records, orphaned A records — enables subdomain takeover and email interception. 6-point DNS hygiene checklist.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-03-06T00:00:00-05:00",
    "dateModified": "2026-03-06T00:00:00-05:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/dns-drift-detection",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/dns-drift-detection",
    "keywords": ["DNS drift detection", "dangling DNS security", "CNAME takeover", "stale DNS records", "DNS monitoring"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "DNS Drift Detection" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="DNS Drift Detection: Find Stale Records | DriftAlarm"
        description="Discover how DNS drift — dangling CNAMEs, stale MX records, orphaned A records — enables subdomain takeover and email interception. 6-point DNS hygiene checklist."
        path="/guides/dns-drift-detection"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/dns-drift-detection')].filter(Boolean)}
      />

      {/* Navigation Bar */}
      <PublicHeader activeLink="guides" />

      {/* Main Content */}
      <main className="guide-content">
        {/* Hero Section */}
        <section className="guide-hero">
          <h1>DNS Drift: How Attackers Exploit Stale Records</h1>
          <GuideByline path="/guides/dns-drift-detection" />
          <GuideAnswer path="/guides/dns-drift-detection" />
          <p className="guide-hero-intro">
            DNS drift is the silent accumulation of stale, orphaned, and misconfigured DNS records
            that occurs as infrastructure changes but DNS zones don't keep pace. Dangling CNAMEs,
            forgotten MX records, and orphaned A records create footholds that attackers actively
            hunt for subdomain takeover, email interception, and brand abuse.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>What DNS drift is and why it's an emerging attack vector</li>
              <li>Common causes: cloud decommission, provider migration, forgotten records</li>
              <li>How attackers exploit dangling CNAMEs, stale MX, and orphaned A records</li>
              <li>Manual vs continuous DNS drift detection methods</li>
              <li>A 6-point DNS hygiene checklist you can use today</li>
              <li>How continuous monitoring catches drift before attackers do</li>
            </ul>
          </div>

          <span className="guide-card-read-time" style={{ display: 'inline-block', marginBottom: '20px' }}>14 min read</span>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/guides/configuration-drift" className="da-btn da-btn--secondary">
              Back to Configuration Drift Guide
            </Link>
          </div>
        </section>

        {/* Table of Contents */}
        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#what-is-dns-drift">What Is DNS Drift?</a></li>
            <li><a href="#common-causes">Common Causes of DNS Drift</a></li>
            <li><a href="#attacker-abuse">How Attackers Exploit DNS Drift</a></li>
            <li><a href="#real-world-impact">Real-World Impact</a></li>
            <li><a href="#detection-methods">Detection: Manual vs Continuous</a></li>
            <li><a href="#dns-hygiene-checklist">DNS Hygiene Checklist</a></li>
            <li><a href="#how-driftalarm-monitors">How DriftAlarm Monitors DNS Changes</a></li>
            <li><a href="#related-guides">Related Security Guides</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        {/* Section: What Is DNS Drift? */}
        <section id="what-is-dns-drift" className="guide-section">
          <h2>What Is DNS Drift?</h2>
          <p>
            <strong>DNS drift</strong> is the gradual divergence between your DNS zone files and
            your actual infrastructure. It happens when DNS records are created for services,
            applications, or cloud resources that are later decommissioned, migrated, or
            reconfigured — but the corresponding DNS entries are never cleaned up.
          </p>
          <p>
            Every organization with more than a handful of DNS records accumulates drift over time.
            A CNAME pointing to a decommissioned Azure resource. An A record for a server that was
            deprovisioned six months ago. An MX record for an email provider you stopped using last
            year. Each of these is a potential entry point for an attacker.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">Defining an Emerging Term</div>
            <div className="callout-content">
              <p>
                While "configuration drift" is well-established in infrastructure management,
                <strong> DNS drift</strong> specifically describes the subset of drift that occurs
                in DNS zones. It's an area that falls between traditional vulnerability management
                and DNS administration — often owned by neither team, monitored by neither toolset.
              </p>
            </div>
          </div>

          <p>
            The danger of DNS drift is its invisibility. Stale records don't generate errors, don't
            trigger alerts in most monitoring systems, and don't show up in vulnerability scans.
            They sit quietly in your zone files until an attacker checks whether the resource on
            the other end of that record is still under your control.
          </p>

          <h3>Why DNS Drift Is Different from Other Misconfigurations</h3>
          <p>
            Unlike an open port or an expired certificate, a dangling DNS record doesn't look broken.
            The DNS resolution works. The record responds. The problem is that it points to something
            you no longer own — and that someone else can claim.
          </p>
          <ul>
            <li><strong>Not detected by port scans:</strong> The record resolves, but the target may respond normally under attacker control</li>
            <li><strong>Not detected by vulnerability scanners:</strong> There's no CVE for a stale CNAME</li>
            <li><strong>Not detected by uptime monitors:</strong> The subdomain may respond with HTTP 200 — just not your content</li>
            <li><strong>Grows over time:</strong> Every infrastructure change that doesn't include DNS cleanup adds drift</li>
          </ul>
        </section>

        {/* Section: Common Causes */}
        <section id="common-causes" className="guide-section">
          <h2>Common Causes of DNS Drift</h2>
          <p>
            DNS drift doesn't happen because of a single mistake. It accumulates through routine
            operations that lack DNS cleanup steps. Here are the most frequent causes.
          </p>

          <h3>Cloud Resource Decommission</h3>
          <p>
            This is the most common source of DNS drift. A team provisions a cloud resource —
            an Azure App Service, an AWS S3 bucket, a CloudFront distribution — and creates a
            CNAME to point a subdomain to it. Months later, the resource is deleted, but the
            CNAME remains. The subdomain now points to an unclaimed cloud endpoint that an
            attacker can register.
          </p>

          <h3>Provider Migration</h3>
          <p>
            When organizations switch hosting providers, CDNs, email services, or SaaS platforms,
            old DNS records often survive the migration. The new provider's records get created,
            but the legacy records pointing to the old provider stay in the zone. If the old
            provider allows new customers to claim those endpoints, the door is open.
          </p>

          <h3>Forgotten Records from Past Projects</h3>
          <p>
            Staging environments, proof-of-concept deployments, marketing campaign microsites,
            and temporary integrations all generate DNS records. When the project ends, the
            records rarely get cleaned up. These forgotten records accumulate over months and
            years into a significant attack surface.
          </p>

          <h3>Organizational Turnover</h3>
          <p>
            When the engineer who created the DNS records leaves the organization, institutional
            knowledge about what each record is for and whether it's still needed leaves with them.
            Without documentation, nobody knows which records are safe to remove.
          </p>

          <div className="callout callout-warning">
            <div className="callout-title">DNS Zones Are Append-Only in Practice</div>
            <div className="callout-content">
              <p>
                Most organizations add DNS records freely but rarely remove them. The perceived risk
                of deleting a record that might still be needed outweighs the invisible risk of
                keeping a stale one. This asymmetry is why DNS drift is so pervasive.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Attacker Abuse Patterns */}
        <section id="attacker-abuse" className="guide-section">
          <h2>How Attackers Exploit DNS Drift</h2>
          <p>
            Stale DNS records aren't just messy — they're exploitable. Attackers have refined techniques
            for discovering and abusing each type of DNS drift. Here are the three primary attack patterns.
          </p>

          <h3>Dangling CNAME &rarr; Subdomain Takeover</h3>
          <p>
            This is the most well-known DNS drift attack. When a CNAME record points to a
            cloud service that has been deprovisioned, an attacker can register the same
            service name on the cloud platform and serve their own content on your subdomain.
          </p>

          <div className="example-box">
            <div className="example-title">Subdomain Takeover Scenario</div>
            <div className="example-content">
              <p>
                <strong>Setup:</strong> Your organization created <code>blog.example.com</code> as a CNAME
                to <code>example-blog.azurewebsites.net</code>. The Azure App Service was later deleted.
              </p>
              <p>
                <strong>Attack:</strong> An attacker creates a new Azure App Service
                named <code>example-blog</code>, which Azure allows because the name is now available.
                The attacker's content is now served at <code>blog.example.com</code>.
              </p>
              <p>
                <strong>Impact:</strong> The attacker controls content on your domain. They can host
                phishing pages, steal cookies scoped to <code>.example.com</code>, or distribute malware
                under your brand.
              </p>
            </div>
          </div>

          <p>Cloud platforms commonly vulnerable to this pattern include:</p>
          <ul>
            <li>Azure App Service, Azure CDN, Azure Traffic Manager</li>
            <li>AWS Elastic Beanstalk, AWS S3 website hosting, CloudFront</li>
            <li>GitHub Pages, Heroku, Shopify, Fastly</li>
            <li>Any platform where custom domain names are registered per-account</li>
          </ul>

          <h3>Stale MX Records &rarr; Email Interception</h3>
          <p>
            When MX records point to a mail server or email service that your organization no
            longer controls, an attacker who gains control of that endpoint can intercept email
            sent to your domain.
          </p>

          <div className="callout callout-critical">
            <div className="callout-title">Email Interception Enables Credential Theft</div>
            <div className="callout-content">
              <p>
                Intercepting email sent to your domain gives attackers access to password reset
                flows, MFA enrollment emails, business correspondence, and potentially
                privileged communications. A stale MX record is not just an email issue — it's
                a full account compromise vector.
              </p>
            </div>
          </div>

          <p>
            This attack is less common than CNAME takeover but potentially more damaging. Email
            interception is harder to detect — the legitimate owner may not realize emails are
            being silently copied or redirected.
          </p>

          <h3>Orphaned A Records &rarr; IP Reuse</h3>
          <p>
            When an A record points to an IP address that your organization has released — a
            deprovisioned cloud VM, a terminated hosting account — that IP address returns to
            the cloud provider's pool. If an attacker (or anyone) is assigned that IP, your
            DNS record now points traffic to their server.
          </p>
          <p>
            While IP reuse attacks require some luck or persistence, cloud providers allocate
            and release IPs frequently. Researchers have demonstrated that previously-used cloud
            IPs can be acquired by repeatedly provisioning and releasing resources in the same
            region. Once an attacker holds the target IP, they receive all traffic directed to
            it by your stale A record.
          </p>

          <div className="callout callout-warning">
            <div className="callout-title">Elastic IPs Don't Solve the Problem</div>
            <div className="callout-content">
              <p>
                Reserved or elastic IPs prevent reuse while you hold the reservation, but they
                often get released during decommission. The core issue remains: if the
                infrastructure is gone but the DNS record stays, the record is exploitable.
              </p>
            </div>
          </div>
        </section>

        {/* Section: Real-World Impact */}
        <section id="real-world-impact" className="guide-section">
          <h2>Real-World Impact of DNS Drift</h2>
          <p>
            DNS drift exploits have affected organizations across industries. The consequences
            go beyond technical compromise — they damage trust, enable fraud, and create legal
            liability.
          </p>

          <h3>Credential Theft via Subdomain Takeover</h3>
          <div className="callout callout-critical">
            <div className="callout-title">Cookies Scoped to Parent Domain</div>
            <div className="callout-content">
              <p>
                When an attacker takes over a subdomain, they can read and set cookies scoped
                to the parent domain (<code>.example.com</code>). This means session tokens,
                authentication cookies, and tracking data for your main application may be
                accessible to the attacker. A single dangling CNAME can compromise every
                authenticated session on your primary domain.
              </p>
            </div>
          </div>

          <h3>Phishing Under Your Brand</h3>
          <p>
            A taken-over subdomain is a phishing operator's ideal infrastructure. The URL
            is on your legitimate domain — it passes email filters, browser trust checks,
            and user scrutiny. Attackers can host convincing login pages on
            <code> accounts.yourdomain.com</code> that even security-aware users would trust.
          </p>

          <h3>Brand and Reputation Damage</h3>
          <p>
            Beyond direct attacks, DNS drift can lead to your domain serving attacker-controlled
            content: SEO spam, cryptocurrency scams, malware distribution, or offensive material.
            Search engines index this content under your domain, damaging your SEO authority and
            brand reputation.
          </p>

          <h3>Compliance and Legal Exposure</h3>
          <p>
            If a subdomain takeover leads to data exposure or is used to distribute malware,
            your organization may face regulatory scrutiny. The fact that the attack was enabled
            by a stale DNS record — something within your control — weakens any defense of
            reasonable security measures.
          </p>
        </section>

        {/* Section: Detection Methods */}
        <section id="detection-methods" className="guide-section">
          <h2>DNS Drift Detection: Manual vs Continuous Monitoring</h2>
          <p>
            There are two approaches to detecting DNS drift: periodic manual audits and
            continuous automated monitoring. Here's how they compare.
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Factor</th>
                  <th>Manual Audit (WHOIS/dig)</th>
                  <th>Continuous Monitoring</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Frequency</td>
                  <td>Quarterly or ad-hoc</td>
                  <td>Daily or real-time</td>
                </tr>
                <tr>
                  <td>Coverage</td>
                  <td>Known records only</td>
                  <td>Full zone + discovered records</td>
                </tr>
                <tr>
                  <td>Detection speed</td>
                  <td>Weeks to months after drift occurs</td>
                  <td>Hours to days</td>
                </tr>
                <tr>
                  <td>Scalability</td>
                  <td>Breaks down beyond ~50 records</td>
                  <td>Handles thousands of records</td>
                </tr>
                <tr>
                  <td>Dangling CNAME detection</td>
                  <td>Requires manual resolution check per record</td>
                  <td>Automated resolution + cloud provider fingerprinting</td>
                </tr>
                <tr>
                  <td>Historical tracking</td>
                  <td>Snapshot only</td>
                  <td>Full change history with baselines</td>
                </tr>
                <tr>
                  <td>Cost</td>
                  <td>Staff time (2-8 hours per audit)</td>
                  <td>Tool subscription</td>
                </tr>
                <tr>
                  <td>Alerting</td>
                  <td>None — issues found only during audit</td>
                  <td>Real-time alerts on drift events</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Manual Audit Process</h3>
          <p>
            If you're starting from zero, a manual audit is a reasonable first step. Export your
            DNS zone, then for each record:
          </p>
          <ol>
            <li>Resolve the record and verify the target still responds</li>
            <li>For CNAMEs, check whether the target resource still exists on the cloud platform</li>
            <li>For A records, verify you still own/control the IP address</li>
            <li>For MX records, confirm the mail service is active and under your control</li>
            <li>Document each record's purpose and owner</li>
            <li>Remove any record that no longer serves a purpose</li>
          </ol>

          <div className="callout callout-info">
            <div className="callout-title">Manual Audits Are a Starting Point, Not a Solution</div>
            <div className="callout-content">
              <p>
                A quarterly DNS audit is better than nothing, but drift can occur within hours
                of a cloud resource being deprovisioned. By the time your next quarterly audit
                finds a dangling CNAME, an attacker may have already claimed it. The gap
                between audits is the window of exposure.
              </p>
            </div>
          </div>
        </section>

        {/* Mid-Article CTA */}
        <section className="guide-section">
          <div className="guide-cta-section">
            <h3>How DriftAlarm Monitors DNS Changes</h3>
            <p>
              DriftAlarm's discovery scans enumerate your DNS records on every scan cycle,
              establishing a baseline of expected records and their resolution targets. When a
              record's target changes, a new record appears, or a record stops resolving, a
              drift event is generated and evaluated against your configured drift rules.
            </p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">Start Free 30-Day Trial</button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        {/* Section: DNS Hygiene Checklist */}
        <section id="dns-hygiene-checklist" className="guide-section">
          <h2>DNS Hygiene Checklist: 6 Steps to Reduce DNS Drift</h2>
          <p>
            You don't need specialized tooling to start reducing DNS drift today. These six
            practices address the root causes and limit your exposure window.
          </p>

          <div className="step-cards">
            <div className="step-card">
              <div className="step-card-number">1</div>
              <div className="step-card-title">Audit Your Zone Files Quarterly</div>
              <p>
                Export every DNS zone you manage. Walk through each record and verify it still
                points to infrastructure you control. Flag any record where the target doesn't
                resolve, returns an error page, or belongs to a decommissioned service. Remove
                confirmed stale records immediately.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">2</div>
              <div className="step-card-title">Add DNS Cleanup to Decommission Checklists</div>
              <p>
                Every infrastructure decommission process — VM teardown, cloud resource deletion,
                service migration — must include a DNS cleanup step. Before releasing an IP
                address or deleting a cloud resource, remove or update all DNS records that
                point to it.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">3</div>
              <div className="step-card-title">Tag DNS Records with Purpose and Owner</div>
              <p>
                Maintain a record-level inventory that documents why each DNS entry exists,
                which project or service it supports, and who is responsible for it. When that
                person leaves or the project ends, the record can be evaluated for removal.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">4</div>
              <div className="step-card-title">Use Wildcard Records Sparingly</div>
              <p>
                Wildcard DNS records (<code>*.example.com</code>) mask individual subdomain
                drift because every subdomain resolves regardless of whether a specific record
                exists. If you use wildcards, monitor the resources behind them closely.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">5</div>
              <div className="step-card-title">Verify CNAME Targets Before and After Cloud Changes</div>
              <p>
                Before deleting any cloud resource that has a custom domain, check for CNAME
                records pointing to it. After deletion, verify the CNAME has been removed.
                Many cloud platforms provide custom domain listings that you can cross-reference
                with your DNS zones.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">6</div>
              <div className="step-card-title">Monitor for Subdomain Takeover Fingerprints</div>
              <p>
                Cloud platforms return characteristic error pages when a custom domain points
                to a non-existent resource. Periodically resolve your subdomains and check for
                these fingerprints — messages like "There isn't a GitHub Pages site here" or
                "NoSuchBucket" indicate a takeover-ready dangling record.
              </p>
            </div>
          </div>
        </section>

        {/* Section: How DriftAlarm Monitors DNS */}
        <section id="how-driftalarm-monitors" className="guide-section">
          <h2>How DriftAlarm Monitors DNS Drift</h2>
          <p>
            DriftAlarm treats DNS records as part of your attack surface baseline. Here's how
            the platform approaches DNS drift detection.
          </p>

          <h3>Discovery-Based DNS Enumeration</h3>
          <p>
            Each discovery scan enumerates DNS records for your monitored domains — A, AAAA,
            CNAME, MX, TXT, NS, and SOA records. These records are stored as part of your
            asset baseline, creating a historical record of your DNS posture over time.
          </p>

          <h3>Drift Rules for DNS Changes</h3>
          <p>
            DriftAlarm's drift detection engine compares each scan's DNS results against your
            established baseline. Built-in drift rules can trigger alerts when:
          </p>
          <ul>
            <li>A new DNS record appears that wasn't in the previous baseline</li>
            <li>An existing record's target changes (e.g., CNAME points to a different host)</li>
            <li>A record stops resolving (potential dangling record)</li>
            <li>MX records change unexpectedly</li>
            <li>NS delegation changes, which could indicate domain hijacking</li>
          </ul>

          <h3>Alerting and Response</h3>
          <p>
            When a DNS drift event matches a configured rule, DriftAlarm generates an alert
            through your configured notification channels. The alert includes the specific
            record that changed, what the previous value was, and the current state — giving
            you the context needed to determine whether the change was intentional or a
            potential security issue.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">Drift Detection Across All Tiers</div>
            <div className="callout-content">
              <p>
                DNS drift monitoring is available on all DriftAlarm tiers, including the 30-day
                trial. Domain discovery scans run daily on every tier; IP and CIDR assets are
                scanned weekly. Higher tiers raise asset counts and scan budgets rather than
                unlocking a faster cadence.</p>
            </div>
          </div>
        </section>

        {/* Section: Related Security Guides */}
        <section id="related-guides" className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            DNS drift is one component of the broader configuration drift problem. Explore these
            related resources for a complete picture.
          </p>

          <div className="playbook-links">
            <Link to="/guides/configuration-drift" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Configuration Drift Guide (Pillar)</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/tls-ssl-drift" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">TLS/SSL Certificate Drift Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/attack-surface-monitoring" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Attack Surface Monitoring Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/dns-drift-monitoring" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">DNS Drift Monitoring — How It Works</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/domain-monitoring" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Domain Monitoring — Continuous Discovery</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="guide-section">
          <div className="callout callout-info">
            <div className="callout-title">Start Monitoring Your DNS</div>
            <div className="callout-content">
              <p>
                Continuous DNS monitoring starts at $99/mo. DriftAlarm scans your domains daily,
                baselines every DNS record, and alerts you when records change or become stale.
              </p>
            </div>
          </div>

          <div className="guide-cta-group" style={{ marginTop: '30px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/upgrade" className="da-btn da-btn--secondary">View Pricing</Link>
          </div>
        </section>
        <GuideFAQ path="/guides/dns-drift-detection" />
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}

export default DNSDriftGuide;
